<template>
  <div class="bg-white border border-black/6 rounded-[20px] p-6 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
    <div class="flex items-center justify-between mb-4">
      <span class="text-[11px] font-bold uppercase tracking-[0.06em] text-muted">Timeline</span>
      <a href="#" @click.prevent="$emit('addDeadline')" class="text-[13px] font-medium text-primary no-underline transition-colors duration-150 hover:opacity-80 hover:no-underline">+ Add date</a>
    </div>

    <div v-if="timelineEvents.length === 0" class="flex flex-col items-center py-5 pb-2 text-muted">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
      <p class="text-[13px] text-placeholder mt-2 mb-0">No key dates set</p>
    </div>

    <div v-else class="flex flex-col relative before:content-[''] before:absolute before:left-[10px] before:top-3 before:bottom-3 before:w-0.5 before:bg-border">
      <div
        v-for="(event, idx) in timelineEvents"
        :key="event.type + '-' + idx"
        :class="getEventClasses(event)">

        <!-- Auto milestone -->
        <template v-if="event.type === 'auto'">
          <div class="absolute left-[6px] w-2.5 h-2.5 rounded-full z-[1]" :style="{ background: event.dotColor }"></div>
          <div class="flex items-baseline gap-2">
            <span class="text-[13px] text-muted font-medium">{{ event.label }}</span>
            <span v-if="event.date" class="text-xs text-placeholder">{{ event.date | formatDate }}</span>
          </div>
        </template>

        <!-- User date -->
        <template v-else>
          <div class="flex items-start gap-2.5 flex-1 min-w-0">
            <button
              class="shrink-0 w-[22px] h-[22px] rounded-full border-2 bg-white cursor-pointer flex items-center justify-center transition-all duration-150 mt-px p-0 relative z-[1]"
              :class="event.completed ? 'bg-[#2d7a0e] border-[#2d7a0e] text-white' : 'border-black/8 hover:border-primary'"
              @click="$emit('toggleDeadline', event.index)">
              <svg v-if="event.completed" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
            </button>

            <div class="flex flex-col min-w-0">
              <span class="text-sm font-medium leading-tight" :class="event.completed ? 'line-through text-muted' : 'text-ink'">
                {{ event.label }}
              </span>
              <span v-if="event.note" class="text-xs text-muted mt-0.5 whitespace-nowrap overflow-hidden text-ellipsis">{{ event.note }}</span>
            </div>
          </div>

          <div class="flex items-center gap-3 shrink-0">
            <div class="text-right flex flex-col">
              <span class="text-[13px] text-[#645B66] font-medium">{{ event.date | formatDate }}</span>
              <span v-if="!event.completed" class="text-[11px] mt-px" :class="relativeClasses(event)">
                {{ getRelativeDate(event.date) }}
              </span>
              <span v-else class="text-[11px] mt-px text-[#2d7a0e] font-semibold">Done</span>
            </div>
            <div class="group/actions flex gap-1 opacity-40 transition-opacity duration-150 hover:opacity-100">
              <button type="button" @click="$emit('editDeadline', event.index)" class="flex items-center justify-center w-7 h-7 rounded-lg text-muted bg-transparent border-0 cursor-pointer transition-all duration-150 hover:bg-[#f4f5fa] hover:text-ink" title="Edit" aria-label="Edit deadline">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
              </button>
              <template v-if="confirmDeleteIndex === event.index">
                <button type="button" @click="confirmDelete(event.index)" class="flex items-center justify-center px-2 h-7 rounded-lg text-xs font-semibold text-[#e53935] bg-transparent border-0 cursor-pointer transition-all duration-150 hover:bg-[#fff5f5]">Delete?</button>
                <button type="button" @click="confirmDeleteIndex = null" class="flex items-center justify-center px-1.5 h-7 rounded-lg text-xs font-medium text-muted bg-transparent border-0 cursor-pointer transition-all duration-150 hover:bg-canvas hover:text-ink">Cancel</button>
              </template>
              <button v-else type="button" @click="confirmDeleteIndex = event.index" class="flex items-center justify-center w-7 h-7 rounded-lg text-muted bg-transparent border-0 cursor-pointer transition-all duration-150 hover:bg-[#fff5f5] hover:text-[#e53935]" title="Delete" aria-label="Delete deadline">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Summary bar -->
    <div v-if="deadlines && deadlines.length > 0" class="flex gap-2 mt-4 pt-4 border-t border-[#f5f3f7]">
      <span v-if="overdueCount > 0" class="text-[11px] font-semibold py-0.5 px-2.5 rounded-full bg-[#ffebee] text-[#e53935]">
        {{ overdueCount }} overdue
      </span>
      <span v-if="soonCount > 0" class="text-[11px] font-semibold py-0.5 px-2.5 rounded-full bg-[#fff3e0] text-[#f57c00]">
        {{ soonCount }} upcoming
      </span>
      <span v-if="completedCount > 0" class="text-[11px] font-semibold py-0.5 px-2.5 rounded-full bg-[#e8f5e9] text-[#2d7a0e]">
        {{ completedCount }} done
      </span>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
dayjs.extend(relativeTime)

export default {
  name: 'deadline-tracker',
  data () {
    return {
      confirmDeleteIndex: null
    }
  },
  props: {
    deadlines: {
      type: Array,
      default: () => []
    },
    applicant: {
      type: Object,
      default: null
    }
  },
  computed: {
    timelineEvents () {
      const events = []

      // Auto milestones from applicant
      if (this.applicant) {
        if (this.applicant.createdAt) {
          const date = this.normalizeDate(this.applicant.createdAt)
          events.push({
            type: 'auto',
            label: 'Client added',
            date: date,
            sortDate: date,
            dotColor: '#7c5bea'
          })
        }

        if (this.applicant.paid === true) {
          const fallback = this.applicant.createdAt ? this.normalizeDate(this.applicant.createdAt) : null
          events.push({
            type: 'auto',
            label: 'Payment received',
            date: null,
            sortDate: fallback,
            dotColor: '#2d7a0e'
          })
        }

        if (this.applicant.deadline) {
          const date = this.normalizeDate(this.applicant.deadline)
          events.push({
            type: 'auto',
            label: 'Application deadline',
            date: date,
            sortDate: date,
            dotColor: '#131415'
          })
        }
      }

      // User deadlines (preserve original index for emit)
      const deadlines = this.deadlines || []
      deadlines.forEach((d, i) => {
        events.push({
          type: 'user',
          label: d.label,
          date: d.date,
          sortDate: d.date,
          note: d.note,
          completed: d.completed || false,
          index: i
        })
      })

      // Sort: completed user items at bottom, then chronologically
      return events.sort((a, b) => {
        const aCompleted = a.type === 'user' && a.completed
        const bCompleted = b.type === 'user' && b.completed
        if (aCompleted && !bCompleted) return 1
        if (!aCompleted && bCompleted) return -1

        if (!a.sortDate && b.sortDate) return 1
        if (a.sortDate && !b.sortDate) return -1
        if (!a.sortDate && !b.sortDate) return 0

        return new Date(a.sortDate) - new Date(b.sortDate)
      })
    },
    overdueCount () {
      if (!this.deadlines) return 0
      return this.deadlines.filter(d => this.isOverdue(d) && !d.completed).length
    },
    soonCount () {
      if (!this.deadlines) return 0
      return this.deadlines.filter(d => this.isSoon(d) && !this.isOverdue(d) && !d.completed).length
    },
    completedCount () {
      if (!this.deadlines) return 0
      return this.deadlines.filter(d => d.completed).length
    }
  },
  methods: {
    confirmDelete (index) {
      this.confirmDeleteIndex = null
      this.$emit('deleteDeadline', index)
    },
    normalizeDate (val) {
      if (!val) return null
      if (typeof val.toDate === 'function') return val.toDate().toISOString()
      if (val.seconds) return new Date(val.seconds * 1000).toISOString()
      return val
    },
    getEventClasses (event) {
      if (event.type === 'auto') {
        return 'flex items-center py-2 pl-8 relative'
      }
      const base = 'flex items-center justify-between py-3 gap-3'
      if (this.isOverdue(event) && !event.completed) {
        return base + ' bg-[#fff5f5] -mx-3 px-3 rounded-lg'
      }
      if (this.isSoon(event) && !this.isOverdue(event) && !event.completed) {
        return base + ' bg-[#fffaf0] -mx-3 px-3 rounded-lg'
      }
      if (event.completed) {
        return base + ' opacity-60'
      }
      return base
    },
    relativeClasses (event) {
      if (this.isOverdue(event)) return 'text-[#e53935] font-semibold'
      if (this.isSoon(event)) return 'text-[#f57c00] font-semibold'
      return 'text-placeholder'
    },
    isOverdue (deadline) {
      return dayjs(deadline.date).isBefore(dayjs(), 'day')
    },
    isSoon (deadline) {
      const daysUntil = dayjs(deadline.date).diff(dayjs(), 'day')
      return daysUntil <= 14 && daysUntil >= 0
    },
    getRelativeDate (date) {
      if (!date) return ''
      const d = dayjs(date)
      const now = dayjs()
      const diff = d.diff(now, 'day')

      if (diff < 0) return Math.abs(diff) + 'd overdue'
      if (diff === 0) return 'Today'
      if (diff === 1) return 'Tomorrow'
      if (diff <= 14) return diff + ' days left'
      return d.fromNow()
    }
  },
  mounted () {
    this._resetConfirmDelete = () => { this.confirmDeleteIndex = null }
    document.addEventListener('click', this._resetConfirmDelete)
  },
  beforeDestroy () {
    if (this._resetConfirmDelete) {
      document.removeEventListener('click', this._resetConfirmDelete)
    }
  }
}
</script>
